import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FinancialBillInstallment } from '@gipi-financial/bill/models/bill-installment.model';
import { FinancialPaths } from '@gipi-paths/financial.paths';
import { BaseFilterDTO } from '@gipi-shared/models/dto/base-filter.dto';
import { PdfUtil } from '@gipi-shared/utils/pdf.util';
import { ArrayUtil, GIPIAbstractCrudService, GIPIBaseService, GIPIUuid, ObjectUtil, StringUtil } from '@gipisistemas/ng-core';
import { Observable } from 'rxjs';
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators';
import { BilletBillet } from '../models/billet.model';
import { BilletAutomaticErrorDTO } from '../models/dto/billet-automatic-error.dto';
import { BilletGenerateResponseDTO } from '../models/dto/generate-billet-error.dto';

@Injectable({ providedIn: 'root' })
export class FinancialBilletFinancialService extends GIPIAbstractCrudService<BilletBillet, BaseFilterDTO> {

    constructor(protected baseService: GIPIBaseService,) {
        super(FinancialPaths.billet, 'v1', baseService);
    }

    download(installmentIds: GIPIUuid[]): Observable<any> {
        return this.baseService.httpClient.post(this.url('download'), installmentIds, { ...this.options(), observe: 'response', responseType: 'blob' as 'json' }).pipe(
            map(this.handleMapper),
            takeUntil(this.handleDestroy),
            catchError(this.handleError),
        );
    }

    generateBillet(
        installment: FinancialBillInstallment,
        generatePdf: boolean
    ): Observable<BilletGenerateResponseDTO> {
        const httpParams = new HttpParams().set('generatePdf', String(generatePdf));

        return this.baseService.httpClient
            .post<Blob>(
                this.url('generate-billet'),
                installment,
                { ...this.options(httpParams), responseType: 'blob' as 'json', observe: 'response' }
            ).pipe(
                mergeMap(async response => {
                    const contentType = response.headers.get('Content-Type') || '';

                    if (contentType.includes('application/json')) {
                        const text = await this._blobToText(response.body as Blob); // Blob -> string
                        const json = JSON.parse(text) as BilletAutomaticErrorDTO;
                        return { isPdf: false, resp: json };
                    }

                    return { isPdf: true, resp: response.body };
                }),
                takeUntil(this.handleDestroy),
                catchError(this.handleError)
            );
    }

    generateBilletAll(billInstallmentList: FinancialBillInstallment[], generatePdf: boolean): Observable<BilletGenerateResponseDTO> {
        const httpParams: HttpParams = new HttpParams().append('generatePdf', String(generatePdf));

        return this.baseService.httpClient
            .post<Blob>(
                this.url('generate-billet-all'),
                billInstallmentList,
                { ...this.options(httpParams), responseType: 'blob' as 'json', observe: 'response' }
            ).pipe(
                mergeMap(async response => {
                    const contentType = response.headers.get('Content-Type') || '';

                    if (contentType.includes('application/json')) {
                        const text = await this._blobToText(response.body as Blob); // Blob -> string
                        const json = JSON.parse(text) as BilletAutomaticErrorDTO;
                        return { isPdf: false, resp: json };
                    }

                    return { isPdf: true, resp: response.body };
                }),
                takeUntil(this.handleDestroy),
                catchError(this.handleError)
            );
    }

    private _blobToText(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsText(blob);
        });
    }

    generateBilletForBillInstallmentWithoutBillet(): Observable<any> {
        return this.baseService.httpClient.post(this.url('generate-billet-for-bill-installment-without-billet'), null, this.options()).pipe(
            map(this.handleMapper),
            takeUntil(this.handleDestroy),
            catchError(this.handleError),
        );
    }

    sendBilletForBank(billInstallmentIdList: GIPIUuid[]): Observable<BilletAutomaticErrorDTO[]> {
        return this.baseService.httpClient.post(
            this.url('send-billet-for-bank'),
            billInstallmentIdList.map(id => String(id)),
            this.options()
        ).pipe(
            map(this.handleMapper),
            takeUntil(this.handleDestroy),
            catchError(this.handleError),
        )
    }

    public async sendOrPrintBillet(billetErrors: BilletAutomaticErrorDTO[], generatePdf: boolean, countResend: number = 0): Promise<void> {
        const installmentIds = billetErrors.map(e => e.billInstallmentId);
        if (ArrayUtil.isEmpty(installmentIds)) {
            return;
        }

        if (countResend >= 3) {
            const isConfirmed: boolean = await this.baseService.confirmationService.confirm({
                title: 'Aviso',
                message: 'Não foi possível enviar o boleto, mesmo após várias tentativas. Verifique o erro informado abaixo.',
                width: '30%',
                type: 'warning',
                listMessage: billetErrors.map(e => e.messageError),
            });

            if (isConfirmed) {
                if (generatePdf) {
                    await this._downloadBillets(installmentIds);
                }
                return;
            }
        }
        countResend++;

        const isConfirmed: boolean = await this.baseService.confirmationService.confirm({
            title: 'Erro no envio',
            message: 'Ocorreu um erro no envio do boleto. Deseja tentar novamente?',
        });

        if (isConfirmed) {
            const newBilletErrors: BilletAutomaticErrorDTO[] = await this.sendBilletForBank(installmentIds).toPromise().catch(error => {
                this.handleMapper(error);
                return [];
            });

            if (!ArrayUtil.isEmpty(newBilletErrors)) {
                await this.sendOrPrintBillet(newBilletErrors, generatePdf, countResend);
            } else if (generatePdf) {
                await this._downloadBillets(installmentIds);
            }
        } else if (generatePdf) {
            await this._downloadBillets(installmentIds);
        }
    }

    private async _downloadBillets(installmentIds: GIPIUuid[]): Promise<void> {
        const file: Blob = await this.download(installmentIds).toPromise()
            .then(resp => new Blob([resp.body], { type: 'application/pdf' }))
            .catch(_ => {
                return null;
            });

        if (!ObjectUtil.isNull(file)) {
            const fileURL = URL.createObjectURL(file as Blob);
            if (!StringUtil.isEmpty(fileURL)) {
                PdfUtil.print(fileURL);
            }
        }
    }

}
